import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Title } from '@/common/title/Title';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';

export const ApiDocs = () => {
	const module = useModuleFromRoute();
	const rsrc = useRsrc();
	const theme = useTheme();
	const title = 'API Docs';
	const swaggerUrl = `/${module}/api/v3/api-docs`;
	const authHeader = rsrc.authHeaders().Authorization;
	const requestInterceptor = useMemo(() => {
		return (req) => {
			if (authHeader) {
				req.headers = req.headers || {};
				req.headers.Authorization = authHeader;
			}
			return req;
		};
	}, [authHeader]);

	const pageStyle = {
		display: 'flex',
		flexDirection: 'column',
		gap: '12px',
		height: '100%',
		minHeight: 0,
	};

	const iframeWrapStyle = {
		flex: 1,
		minHeight: 0,
		borderRadius: '12px',
		overflow: 'auto',
		background: theme.palette.mode !== 'dark' ? theme.palette.primary.light : theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
	};

	const swaggerWrapStyle = {
		height: '100%',
	};

	const swaggerStyleOverrides = useMemo(() => {
		return `
			.swagger-ui {
				color: ${theme.palette.text.primary};
				background: ${theme.palette.mode !== 'dark' ? theme.palette.primary.light : theme.palette.background.paper};
				font-family: ${theme.typography.fontFamily};
			}

			.swagger-ui a {
				color: ${theme.palette.primary.main};
			}

			.swagger-ui a:hover {
				color: ${theme.palette.primary.dark};
			}

			.swagger-ui .info .title,
			.swagger-ui .opblock-tag,
			.swagger-ui .parameter__name,
			.swagger-ui .response-col_status,
			.swagger-ui .model-title {
				color: ${theme.palette.text.primary};
			}

			.swagger-ui .markdown p,
			.swagger-ui .markdown li,
			.swagger-ui .info p,
			.swagger-ui .info li,
			.swagger-ui .parameter__type,
			.swagger-ui .parameter__in,
			.swagger-ui .response-col_description,
			.swagger-ui .model,
			.swagger-ui .tab li {
				color: ${theme.palette.text.secondary};
			}

			.swagger-ui .model-box,
			.swagger-ui .model-box-control,
			.swagger-ui .model-container,
			.swagger-ui section.models,
			.swagger-ui .model-box-control,
			.swagger-ui .parameters,
			.swagger-ui .responses-wrapper,
			.swagger-ui .responses-inner,
			.swagger-ui .table-container,
			.swagger-ui table tbody tr td,
			.swagger-ui table thead tr th,
			.swagger-ui table thead tr td {
				border-color: ${theme.palette.divider};
			}

			.swagger-ui .model-box,
			.swagger-ui .model-box-control,
			.swagger-ui .model-container,
			.swagger-ui section.models,
			.swagger-ui .models-control,
			.swagger-ui .models-control .expand-operation,
			.swagger-ui .model-title {
				background: ${theme.palette.mode !== 'dark' ? theme.palette.primary.light : theme.palette.background.paper};
			}

			.swagger-ui .json-schema-2020-12-accordion,
			.swagger-ui .json-schema-2020-12-accordion > button,
			.swagger-ui .json-schema-2020-12-expand-deep-button,
			.swagger-ui .json-schema-2020-12-expand-deep-button > button,
			.swagger-ui button.json-schema-2020-12-expand-deep-button,
			.swagger-ui button.json-schema-2020-12-accordion {
				background-color: rgba(0,0,0,.005);
			}


			.swagger-ui input[type="text"],
			.swagger-ui input[type="password"],
			.swagger-ui input[type="search"],
			.swagger-ui input[type="email"],
			.swagger-ui input[type="file"],
			.swagger-ui select,
			.swagger-ui textarea {
				background: ${theme.palette.background.paper};
				color: ${theme.palette.text.primary};
				border-color: ${theme.palette.divider};
			}

			.swagger-ui .btn {
				background: ${theme.palette.primary.main};
				color: ${theme.palette.primary.contrastText};
				border-color: ${theme.palette.primary.main};
				box-shadow: none;
			}

			.swagger-ui .btn:hover {
				background: ${theme.palette.primary.dark};
				border-color: ${theme.palette.primary.dark};
			}

			.swagger-ui .btn:disabled {
				background: ${theme.palette.action.disabledBackground};
				color: ${theme.palette.action.disabled};
				border-color: ${theme.palette.action.disabledBackground};
			}

			.swagger-ui .scheme-container {
				display: none;
			}
		`;
	}, [theme]);

	return (
		<div style={pageStyle}>
			<style>{swaggerStyleOverrides}</style>
			<Title icon={far.faInfoCircle} title={title} />
			<div style={iframeWrapStyle}>
				<div style={swaggerWrapStyle}>
					<SwaggerUI
						url={swaggerUrl}
						requestInterceptor={requestInterceptor}
						persistAuthorization
						supportedSubmitMethods={[]}
					/>
				</div>
			</div>
		</div>
	);
};
